import { NavOracleReadAbi, TotalSupplyAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * Share token priced by an **external NAV oracle** — two calls:
 *   1. `shareToken.totalSupply()`
 *   2. `priceOracle.latestRoundData()`
 *
 * For wrappers that are plain ERC-20s with no vault surface at all: Re
 * Protocol's `reUSD`/`reUSDe` expose neither `asset()`, `totalAssets()`,
 * `convertToAssets()` nor any rebase getter — the accrued price lives in
 * a separate Chainlink-shaped oracle that Re re-publishes daily at 00:00
 * UTC. So unlike [nativeWnlp](./nativeWnlp.ts), which at least has a
 * rate getter on the token, here the token itself knows nothing about
 * its own value.
 *
 * ## Scaling
 *
 * The oracle quotes USD per share — which for a USD-pegged underlying is
 * exactly the `exchangeRate` semantic (underlying per share, 1e18). Re's
 * feeds answer in **18 decimals**, so their answer passes through untouched;
 * Theo's RedStone `sthUSD_FUNDAMENTAL` feed answers in **8**, which is why
 * `priceOracleDecimals` exists. Feed decimals are a per-feed fact, not a
 * house convention — reading an 8-decimal answer as 18 would price a $24M
 * vault at 1e-10 of par and, because nothing else in the row disagrees, it
 * would simply render as dust. `totalAssets` is then rebased from share units
 * into raw underlying units, which is where an 18-decimal share over
 * 6-decimal USDC would otherwise be off by 1e12:
 *
 *   totalAssets = totalSupply × answer × underlyingUnit / (1e18 × shareUnit)
 *
 * ## Liquidity
 *
 * Deliberately none — the entry is registered `request-based`, so
 * `fetchPublic` reports `liquidity: 0`. Re *does* run an instant leg,
 * but it is conditional on the redemption buffer exceeding 1 % of supply
 * and further capped per-transaction and per-day, and the buffer is held
 * in **bridged sUSDe** on Ethereum/Base/Arbitrum — which exposes no
 * local `convertToAssets` outside Ethereum, so its USD value is not
 * readable on the chains that would need it. A number that is only
 * correct on one of four chains is worse than an explicit "assume you
 * queue"; see the savings README for the upgrade path.
 */
export const readerNavOracle = (entry: SavingsRegistryEntry): SavingsReader => {
  const shareUnit = 10n ** BigInt(entry.decimals)
  const underlyingUnit =
    10n ** BigInt(entry.underlyingDecimals ?? entry.decimals)
  // Feed decimals → the 1e18 `exchangeRate` scale. 18 is the default because
  // that is what Re's four feeds answer and what this reader was written for.
  const oracleDecimals = entry.priceOracleDecimals ?? 18
  const oracleScale = 10n ** BigInt(oracleDecimals)
  return {
    calls: [
      { address: entry.address, name: 'totalSupply', params: [] },
      {
        address: entry.priceOracle ?? entry.address,
        name: 'latestRoundData',
        params: [],
      },
    ],
    abis: [TotalSupplyAbi, NavOracleReadAbi],
    parse: ([supply, round]) => {
      const totalSupply = toBigInt(supply)
      if (totalSupply === undefined) return undefined

      // `latestRoundData` returns a 5-tuple; the answer is index 1. Some
      // multicall decoders hand back an array, others an object.
      const raw = Array.isArray(round)
        ? round[1]
        : (round as { answer?: unknown } | undefined)?.answer
      const answer = toBigInt(raw)
      // A non-positive NAV is a broken feed, not a zero-value vault —
      // drop the entry rather than publish a 0 price.
      if (answer === undefined || answer <= 0n) return undefined
      const exchangeRate = (answer * ONE_E18) / oracleScale

      return {
        totalAssets:
          (totalSupply * exchangeRate * underlyingUnit) / (ONE_E18 * shareUnit),
        totalSupply,
        exchangeRate,
      }
    },
  }
}
